import { Stack } from '@mui/material';
import { TextBlock } from 'common/ui/TextBlock';
import { useDrawer } from 'services/drawer/hooks';
import Yup from 'utils/validation';
import { Fields, Form, FormRef } from 'common/form';
import { ToastType, useToast } from 'services/toast';
import {
  OrganizationConfig,
  useGetOrganizationConfigByOrganizationIdQuery,
  useUpsertOrganizationConfigMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useRecoilState } from 'recoil';
import { organizationConfigAtom } from 'services/organization/atom';
import { useConfirmationModal } from 'services/modals/hooks';
import { useEffect, useRef } from 'react';
import { Loading } from 'common/ui/Loading';

const MAX_CHARACTERS = 20000;

const validationSchema = Yup.object({
  globalContext: Yup.string()
    .trim()
    .max(
      MAX_CHARACTERS,
      `Reference material must be less than ${MAX_CHARACTERS} characters`
    )
});

export type GlobalContextFormData = Yup.InferType<typeof validationSchema>;

const fields: Fields<GlobalContextFormData> = {
  globalContext: {
    type: 'textarea',
    label: 'Add reference material (optional)',
    subLabel: `Used by Popp for all campaigns in this account.
If there is a conflict with specific job description, Popp prefers the job description.`,
    rows: 20,
    maxCharacters: MAX_CHARACTERS
  }
};

type Props = {
  onSave?: (globalContext: string | undefined) => void;
};

export const GlobalContextDrawer = ({ onSave }: Props) => {
  const { showToast } = useToast();
  const { closeDrawer } = useDrawer();
  const { showModal } = useConfirmationModal();
  const [organizationConfig, setOrganizationConfig] = useRecoilState(
    organizationConfigAtom
  );
  const formRef = useRef<FormRef<GlobalContextFormData>>(null);

  const { loading: isFetching } = useGetOrganizationConfigByOrganizationIdQuery(
    {
      variables: {
        organizationId: organizationConfig?.organizationId || ''
      },
      onCompleted: (result) => {
        setOrganizationConfig(
          // TODO: Replace this forced cast with a proper type guard
          result.getOrganizationConfigByOrganizationId
            ?.items[0] as OrganizationConfig
        );
      }
    }
  );

  useEffect(() => {
    if (formRef.current) {
      formRef.current?.setValue(
        'globalContext',
        organizationConfig?.globalContext || ''
      );
    }
  }, [organizationConfig?.globalContext]);

  const [upsertOrganizationConfig, { loading }] =
    useUpsertOrganizationConfigMutation();

  const executeUpdate = async (data: GlobalContextFormData) => {
    try {
      const globalContext = data.globalContext?.trim();

      const result = await upsertOrganizationConfig({
        variables: {
          input: {
            globalContext
          }
        }
      });

      const updatedOrganizationConfig = result.data?.upsertOrganizationConfig;

      if (updatedOrganizationConfig) {
        setOrganizationConfig((prevConfig) =>
          prevConfig
            ? {
                ...prevConfig,
                globalContext
              }
            : updatedOrganizationConfig
        );
      }

      onSave?.(globalContext);

      showToast({
        type: ToastType.Success,
        message: 'Reference material updated successfully'
      });
      closeDrawer();
    } catch (error) {
      showToast({
        type: ToastType.Error,
        message: 'Failed to update reference material'
      });
    }
  };

  const handleSave = (data: GlobalContextFormData) => {
    showModal({
      title: 'Update reference material?',
      message: 'Any changes will only affect ongoing and future conversations.',
      confirmationText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: () => executeUpdate(data),
      width: 380
    });
  };

  return (
    <Stack width="479px" gap="20px" pb="20px">
      <TextBlock variant="drawer" title="Reference material" />
      <Stack direction="column" gap="20px">
        {isFetching ? (
          <Stack
            height="100%"
            justifyContent="center"
            alignItems="center"
            mt="20px"
          >
            <Loading />
          </Stack>
        ) : (
          <Form
            schema={validationSchema}
            fields={fields}
            sx={{ gap: '20px' }}
            submitButtonProps={{
              label: 'Confirm',
              variant: 'primary',
              disabled: loading || isFetching,
              loading: loading || isFetching
            }}
            cancelButtonProps={{
              label: 'Cancel',
              variant: 'tertiary'
            }}
            onSubmit={handleSave}
            onCancel={closeDrawer}
            defaultValues={{
              globalContext: organizationConfig?.globalContext || ''
            }}
            ref={formRef}
          />
        )}
      </Stack>
    </Stack>
  );
};
